import axios from "axios";

const API_BASE_URL = "/api/";

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

let refreshPromise = null;

const clearExpiredSession = () => {
  [
    "access_token",
    "refresh_token",
    "user",
    "user_type",
    "access_type",
    "login_time",
  ].forEach((key) => localStorage.removeItem(key));

  if (window.location.pathname !== "/") {
    window.location.replace("/");
  }
};

const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem("refresh_token");

  if (!refreshToken) {
    throw new Error("No refresh token is available.");
  }

  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE_URL}auth/token/refresh/`, {
        refresh: refreshToken,
      })
      .then((response) => {
        const access = response.data?.access;

        if (!access) {
          throw new Error("The refresh response did not contain an access token.");
        }

        localStorage.setItem("access_token", access);

        // Preserve a rotated refresh token when the backend returns one.
        if (response.data?.refresh) {
          localStorage.setItem("refresh_token", response.data.refresh);
        }

        return access;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");

  if (
    token &&
    !config.url?.includes("auth/login/") &&
    !config.url?.includes("auth/token/refresh/")
  ) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;
    const isLoginRequest = originalRequest.url?.includes("auth/login/");
    const isRefreshRequest = originalRequest.url?.includes(
      "auth/token/refresh/"
    );

    if (
      status === 401 &&
      !isLoginRequest &&
      !isRefreshRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        const access = await refreshAccessToken();
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        clearExpiredSession();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
